import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { setData } from 'platform/forms-system/src/js/actions';
import recordEvent from 'platform/monitoring/record-event';
import { isCaveCanceledError } from '../api/errors';
import {
  getActiveTrackingKeySignature,
  revertRemovedSelections,
} from '../domain/conflictResolution';
import {
  VETERAN_INFO_FIELDS,
  MILITARY_HISTORY_FIELDS,
} from '../domain/fieldMapping';
import { selectCaveEnabled } from '../domain/selectors';
import { processDocumentWithAutoResolve } from '../api/workflow';

const CONFLICT_GROUPS = {
  veteranInfo: VETERAN_INFO_FIELDS,
  militaryHistory: MILITARY_HISTORY_FIELDS,
};

// Purely reactive hook that lives in SurvivorsBenefitsApp.
// Watches formData.files and triggers CAVE processing for any file that has an
// idpDocumentId and isn't already in-flight.
export const useCaveManager = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const inFlightRef = useRef(new Map());

  const caveEnabled = useSelector(selectCaveEnabled);
  const formDataFiles = useSelector(state => state.form?.data?.files);
  const activeTrackingKeySignature = useSelector(state =>
    getActiveTrackingKeySignature(state.form?.data?.files),
  );

  // When the set of files changes, revert any active scanned selection whose
  // source file was removed back to the user's original value, and clear
  // selection markers left stale by manual edits.
  useEffect(() => {
    if (!caveEnabled) return;

    const currentFormData = store.getState().form.data ?? {};
    const revertedData = revertRemovedSelections(
      currentFormData,
      CONFLICT_GROUPS,
    );
    if (revertedData) {
      dispatch(setData(revertedData));
    }
  }, [activeTrackingKeySignature, caveEnabled, dispatch, store]);

  // Continue processing in-flight uploads and status updates on each file-state
  // change.
  useEffect(() => {
    if (!caveEnabled) return;

    const updateFile = (trackingKey, updates) => {
      const state = store.getState().form.data ?? {};
      const nextFiles = (state.files ?? []).map(f =>
        f.idpTrackingKey === trackingKey ? { ...f, ...updates } : f,
      );
      dispatch(setData({ ...state, files: nextFiles }));
    };

    const processFile = fileEntry => {
      const { idpDocumentId, idpTrackingKey } = fileEntry;
      inFlightRef.current.set(idpTrackingKey, idpDocumentId);

      const isFileDeleted = () => {
        const latestFiles = store.getState().form?.data?.files ?? [];
        return !latestFiles.some(f => f?.idpTrackingKey === idpTrackingKey);
      };

      if (isFileDeleted()) {
        if (inFlightRef.current.get(idpTrackingKey) === idpDocumentId) {
          inFlightRef.current.delete(idpTrackingKey);
        }
        return;
      }

      // Immediately mark as fetching-artifacts so Redux reflects the active phase.
      updateFile(idpTrackingKey, { idpUploadStatus: 'fetching-artifacts' });

      recordEvent({
        event: 'cave-document-processing-start',
        'api-name': 'CAVE Document Processing',
        'api-status': 'started',
      });

      const latestFormData = store.getState().form.data ?? {};
      processDocumentWithAutoResolve(
        { id: idpDocumentId },
        latestFormData,
        latestFormData.files ?? [],
        {
          onStatusChange: status => {
            if (!isFileDeleted()) {
              updateFile(idpTrackingKey, { idpUploadStatus: status });
            }
          },
          shouldAbort: isFileDeleted,
        },
      )
        .then(sections => {
          if (isFileDeleted()) {
            if (inFlightRef.current.get(idpTrackingKey) === idpDocumentId) {
              inFlightRef.current.delete(idpTrackingKey);
            }
            return;
          }
          updateFile(idpTrackingKey, {
            idpUploadStatus: 'success',
            idpArtifacts: sections,
          });
          recordEvent({
            event: 'cave-document-processing-success',
            'api-name': 'CAVE Document Processing',
            'api-status': 'successful',
          });
          if (inFlightRef.current.get(idpTrackingKey) === idpDocumentId) {
            inFlightRef.current.delete(idpTrackingKey);
          }
        })
        .catch(error => {
          if (isCaveCanceledError(error) || isFileDeleted()) {
            if (inFlightRef.current.get(idpTrackingKey) === idpDocumentId) {
              inFlightRef.current.delete(idpTrackingKey);
            }
            return;
          }
          updateFile(idpTrackingKey, {
            idpUploadStatus: 'error',
            idpUploadError: error?.message,
          });
          recordEvent({
            event: 'cave-document-processing-failure',
            'api-name': 'CAVE Document Processing',
            'api-status': 'failed',
            // Analytics dimension must be a STABLE, bounded key. CaveError
            // codes (partial_result, canceled, etc.) are a safe set; never push
            // error.message — it can carry document-specific text / PII and
            // would explode GA cardinality. The full message is preserved in
            // idpUploadError above for the per-file UI alert.
            'error-key': error?.code || 'unknown',
          });
          if (inFlightRef.current.get(idpTrackingKey) === idpDocumentId) {
            inFlightRef.current.delete(idpTrackingKey);
          }
        });
    };

    (formDataFiles ?? []).forEach(fileEntry => {
      const { idpUploadStatus, idpDocumentId, idpTrackingKey } =
        fileEntry || {};
      const IN_FLIGHT_STATUSES = new Set([
        'pending',
        'processing',
        'fetching-artifacts',
        'downloading-artifacts',
      ]);
      if (
        IN_FLIGHT_STATUSES.has(idpUploadStatus) &&
        idpDocumentId &&
        idpTrackingKey &&
        inFlightRef.current.get(idpTrackingKey) !== idpDocumentId
      ) {
        processFile(fileEntry);
      }
    });
  }, [caveEnabled, dispatch, formDataFiles, store]);
};
